import apiClient from './api';
import {
  selectDrainBatch,
  markInFlight,
  applyBatchResult,
  revertInFlightToPending,
  resetStaleInFlight as _resetStale,
  reapSilentDropFailures,
} from './mutationStore';

const LOCK_NAME = 'rin-sync-drain';
const DEBOUNCE_MS = 300;

let debounceTimer = null;
let fallbackInFlight = null;

export const resetStaleInFlight = _resetStale;

// Reason: mirrors activateResetUnderLock in sw/sync.js — uses ifAvailable so a
// tab opened mid-drain skips the reset rather than flipping in_flight rows back
// to pending under an active drain.
export async function resetUnderLock() {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(
      LOCK_NAME,
      { mode: 'exclusive', ifAvailable: true },
      async (lock) => {
        if (!lock) return;
        await _resetStale();
      }
    );
  }
  await _resetStale();
}

export async function requestBackgroundSync() {
  if (typeof navigator === 'undefined') return;
  if (!navigator.serviceWorker || !('SyncManager' in (typeof window !== 'undefined' ? window : {}))) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    if (reg.sync) await reg.sync.register('flush-mutations');
  } catch {
    // Background Sync unavailable (Safari/iOS, denied permission, etc.) — no-op.
  }
}

export function triggerDrain() {
  return new Promise((resolve) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      _runUnderLock().then(resolve, resolve);
    }, DEBOUNCE_MS);
  });
}

async function _runUnderLock() {
  // Reason: register Background Sync before the online check so offline writes
  // schedule a future SW flush even when the foreground drain exits early.
  await requestBackgroundSync();
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    await navigator.locks.request(LOCK_NAME, { mode: 'exclusive' }, _drain);
  } else {
    // Fallback mutex for environments without Web Locks (Safari < 15.4)
    if (fallbackInFlight) { await fallbackInFlight; return; }
    fallbackInFlight = _drain().finally(() => { fallbackInFlight = null; });
    await fallbackInFlight;
  }
}

async function _drain() {
  const candidates = await selectDrainBatch();
  const batch = await markInFlight(candidates);
  if (batch.length === 0) return;

  let resp;
  try {
    resp = await apiClient.post('/api/mutations/batch', {
      mutations: batch.map(m => ({ key: m.key, type: m.type, payload: m.payload })),
    });
  } catch (err) {
    await revertInFlightToPending(batch.map(m => m.key), err?.message || err);
    return;
  }

  const results = resp?.data?.results || [];
  await applyBatchResult(results);
  // Reason: server may return a partial or empty results array for a 2xx; any
  // batch key still in_flight was silently omitted — revert so they retry.
  await revertInFlightToPending(batch.map(m => m.key), 'protocol: missing from batch response');
  await reapSilentDropFailures();
}

export const __test_only = {
  resetState: () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
    fallbackInFlight = null;
  },
  flushDebounce: () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  },
  drainNow: _runUnderLock,
};
